/**
 * Payments List Page
 */
import React, { useState, useEffect, useMemo, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Skeleton,
  IconButton,
  Menu,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Select,
  TablePagination,
  CircularProgress,
  ListSubheader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Clear as ClearIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';
import { PageHeader, StatsCard } from '../../components/common';
import {
  useGetPaymentsQuery,
  useGetPaymentsStatisticsQuery,
  useLazyExportPaymentsQuery,
  useUpdatePaymentStatusMutation,
  Payment,
  type UpdatePaymentStatusData,
} from '../../store/api/paymentsApi';
import { usePermissions } from '../../hooks/usePermissions';
import { palette } from '../../theme/palette';
import {
  getAllowedNextPaymentStatuses,
  splitPaymentStatusesForMenu,
  PAYMENT_STATUS_LABELS,
  formatPaymentStatusUpdateError,
  type PaymentStatus,
} from '../../utils/paymentStatusTransitions';
import moment from 'moment';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { formatNumber as formatNumberLocalized } from '../../utils/format';

function getPaymentStatusColor(status: string) {
  switch (status) {
    case 'COMPLETED':
      return 'success';
    case 'PENDING':
      return 'warning';
    case 'PROCESSING':
      return 'info';
    case 'FAILED':
      return 'error';
    case 'REFUNDED':
    case 'PARTIALLY_REFUNDED':
      return 'default';
    default:
      return 'default';
  }
}

function getPaymentStatusIcon(status: string): React.ReactElement | undefined {
  switch (status) {
    case 'COMPLETED':
      return <CheckCircleIcon fontSize="small" />;
    case 'PENDING':
      return <PendingIcon fontSize="small" />;
    case 'FAILED':
      return <CancelIcon fontSize="small" />;
    default:
      return undefined;
  }
}

/** Clickable status (chip + chevron); menu → dialog with notes (same pattern as orders list). */
function PaymentListStatusTrigger({
  payment,
  canUpdate,
  updating,
  onOpenStatusMenu,
}: {
  payment: Payment;
  canUpdate: boolean;
  updating: boolean;
  onOpenStatusMenu: (event: MouseEvent<HTMLElement>, p: Payment) => void;
}) {
  const { t } = useTranslation();
  const allowed = getAllowedNextPaymentStatuses(payment.status);

  if (!canUpdate || allowed.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
        <Chip
          icon={getPaymentStatusIcon(payment.status)}
          label={t(`servicePayments.status.${payment.status}`, payment.status)}
          size="small"
          color={getPaymentStatusColor(payment.status) as 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'}
        />
      </Box>
    );
  }

  return (
    <Box
      component="button"
      type="button"
      disabled={updating}
      onClick={(e) => {
        e.stopPropagation();
        onOpenStatusMenu(e, payment);
      }}
      aria-label={t('servicePayments.aria.changeStatus')}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.25,
        cursor: updating ? 'wait' : 'pointer',
        border: 'none',
        background: 'none',
        padding: 0,
        margin: 0,
        font: 'inherit',
        color: 'inherit',
        minHeight: 40,
        borderRadius: 1,
        '&:hover:not(:disabled)': { opacity: 0.85 },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
      }}
    >
      {updating ? (
        <CircularProgress size={20} thickness={4} />
      ) : (
        <>
          <Chip
            icon={getPaymentStatusIcon(payment.status)}
            label={t(`servicePayments.status.${payment.status}`, payment.status)}
            size="small"
            color={getPaymentStatusColor(payment.status) as 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'}
          />
          <KeyboardArrowDownIcon sx={{ fontSize: '1.125rem', color: 'text.secondary', flexShrink: 0 }} />
        </>
      )}
    </Box>
  );
}

const PaymentsListPage = ({ embedded = false }: { embedded?: boolean } = {}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [paymentStatus, setPaymentStatus] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [exportPayments, { isLoading: isExporting }] = useLazyExportPaymentsQuery();
  const [updatePaymentStatus, { isLoading: isPaymentStatusUpdating }] = useUpdatePaymentStatusMutation();

  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const [statusMenuPayment, setStatusMenuPayment] = useState<Payment | null>(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusDialogPayment, setStatusDialogPayment] = useState<Payment | null>(null);
  const [statusDialogBasisStatus, setStatusDialogBasisStatus] = useState<string>('');
  const [statusNewStatus, setStatusNewStatus] = useState<UpdatePaymentStatusData['status'] | ''>('');
  const [statusAdminNotes, setStatusAdminNotes] = useState('');
  const [statusFailureReason, setStatusFailureReason] = useState('');
  const [updatingPaymentId, setUpdatingPaymentId] = useState<number | null>(null);

  // Check permissions
  const canExportPayments = hasPermission('export_payments');
  const canViewPaymentDetails = hasPermission('view_payment_details');
  const canViewOrderDetails = hasPermission('view_order_details');
  const canUpdatePaymentStatus = hasPermission('update_payment_status');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Payments query
  const { data: paymentsData, isLoading: paymentsLoading } = useGetPaymentsQuery({
    page: page + 1,
    page_size: rowsPerPage,
    search: search || undefined,
    payment_status: paymentStatus || undefined,
    payment_method: paymentMethod || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  });

  // Statistics query
  const { data: statsData } = useGetPaymentsStatisticsQuery();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, payment: Payment) => {
    setAnchorEl(event.currentTarget);
    setSelectedPayment(payment);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPayment(null);
  };

  const closePaymentStatusMenu = () => {
    setStatusMenuAnchor(null);
    setStatusMenuPayment(null);
  };

  const openPaymentStatusMenu = (event: MouseEvent<HTMLElement>, p: Payment) => {
    setStatusMenuAnchor(event.currentTarget);
    setStatusMenuPayment(p);
  };

  const closePaymentStatusDialog = () => {
    setStatusDialogOpen(false);
    setStatusDialogPayment(null);
    setStatusDialogBasisStatus('');
    setStatusNewStatus('');
    setStatusAdminNotes('');
    setStatusFailureReason('');
  };

  const statusMenuSplit = useMemo(() => {
    if (!statusMenuPayment) {
      return { progress: [] as PaymentStatus[], markFailed: [] as PaymentStatus[], refunds: [] as PaymentStatus[] };
    }
    return splitPaymentStatusesForMenu(getAllowedNextPaymentStatuses(statusMenuPayment.status));
  }, [statusMenuPayment]);

  const statusDialogSplit = useMemo(() => {
    if (!statusDialogBasisStatus) {
      return { progress: [] as PaymentStatus[], markFailed: [] as PaymentStatus[], refunds: [] as PaymentStatus[] };
    }
    return splitPaymentStatusesForMenu(getAllowedNextPaymentStatuses(statusDialogBasisStatus));
  }, [statusDialogBasisStatus]);

  const handlePaymentStatusMenuPick = (picked: PaymentStatus) => {
    if (!statusMenuPayment) return;
    setStatusDialogPayment(statusMenuPayment);
    setStatusDialogBasisStatus(statusMenuPayment.status);
    setStatusNewStatus(picked);
    setStatusAdminNotes('');
    setStatusFailureReason('');
    closePaymentStatusMenu();
    setStatusDialogOpen(true);
  };

  const handlePaymentStatusDialogSubmit = async () => {
    if (!statusDialogPayment || !statusNewStatus) return;
    setUpdatingPaymentId(statusDialogPayment.id);
    try {
      const data: UpdatePaymentStatusData = { status: statusNewStatus };
      if (statusAdminNotes) data.admin_notes = statusAdminNotes;
      if (statusNewStatus === 'FAILED' && statusFailureReason.trim()) {
        data.failure_reason = statusFailureReason.trim();
      }
      await updatePaymentStatus({ id: statusDialogPayment.id, data }).unwrap();
      toast.success(t('servicePayments.toast.statusUpdated', { id: statusDialogPayment.id }));
      closePaymentStatusDialog();
    } catch (err) {
      toast.error(formatPaymentStatusUpdateError(err));
    } finally {
      setUpdatingPaymentId(null);
    }
  };

  const handleExport = async () => {
    try {
      const blob = await exportPayments({
        search: search || undefined,
        payment_status: paymentStatus || undefined,
        payment_method: paymentMethod || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      }).unwrap();

      if (blob.type.includes('application/json')) {
        const errorPayload = await blob.text();
        throw new Error(errorPayload || 'Invalid export response');
      }

      const normalizedType = blob.type.toLowerCase();
      const fileExtension = normalizedType.includes('sheet') ? 'xlsx' : 'csv';

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // Locale 'en' forced: filenames must stay ASCII.
      link.download = `payments_${moment().locale('en').format('YYYY-MM-DD')}.${fileExtension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(t('servicePayments.toast.exportSuccess'));
    } catch (err: any) {
      toast.error(err?.data?.message || t('servicePayments.toast.exportFailed'));
    }
  };

  const formatNumber = (num: number): string =>
    formatNumberLocalized(num, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const formatInteger = (num: number): string =>
    formatNumberLocalized(num, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const formatCurrency = (amount: string | number): string => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `EGP ${formatNumber(num)}`;
  };

  const paymentsColumns = [
    { id: 'id', label: t('servicePayments.cols.id'), minWidth: 80 },
    { id: 'order', label: t('servicePayments.cols.order'), minWidth: 150 },
    { id: 'user', label: t('servicePayments.cols.user'), minWidth: 150 },
    { id: 'provider', label: t('servicePayments.cols.provider'), minWidth: 150 },
    { id: 'amount', label: t('servicePayments.cols.amount'), minWidth: 120 },
    { id: 'payment_method', label: t('servicePayments.cols.method'), minWidth: 100 },
    { id: 'status', label: t('servicePayments.cols.status'), minWidth: 120 },
    { id: 'created_at', label: t('servicePayments.cols.created'), minWidth: 120 },
    { id: 'paid_at', label: t('servicePayments.cols.paidAt'), minWidth: 120 },
  ];

  const stats = statsData?.data;

  const exportButton = canExportPayments ? (
    <Button
      variant="outlined"
      startIcon={<DownloadIcon />}
      onClick={handleExport}
      disabled={isExporting}
    >
      {isExporting ? <CircularProgress size={20} /> : t('servicePayments.buttons.export')}
    </Button>
  ) : undefined;

  return (
    <Box sx={{ p: embedded ? 0 : 3 }}>
      {embedded
        ? exportButton && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>{exportButton}</Box>
          )
        : (
          <PageHeader
            title={t('servicePayments.title')}
            breadcrumbs={[
              { label: t('servicePayments.breadcrumbs.dashboard'), path: '/' },
              { label: t('servicePayments.breadcrumbs.payments') },
            ]}
            action={exportButton}
          />
        )}

      {/* Statistics Cards */}
      {stats && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)',
            },
            gap: 2,
            mb: 3,
          }}
        >
          <StatsCard
            title={t('servicePayments.stats.totalRevenue')}
            value={formatCurrency(stats.overview.total_revenue)}
            icon={<MoneyIcon />}
            color={palette.status.success}
          />
          <StatsCard
            title={t('servicePayments.stats.pendingAmount')}
            value={formatCurrency(stats.overview.pending_amount)}
            icon={<PendingIcon />}
            color={palette.status.warning}
          />
          <StatsCard
            title={t('servicePayments.stats.totalPayments')}
            value={formatInteger(stats.overview.total_payments)}
            icon={<MoneyIcon />}
            color={palette.brand[500]}
          />
          <StatsCard
            title={t('servicePayments.stats.averagePayment')}
            value={formatCurrency(stats.overview.average_payment)}
            icon={<MoneyIcon />}
            color={palette.brandSecondary[500]}
          />
        </Box>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <TextField
              placeholder={t('servicePayments.filters.searchPlaceholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              size="small"
              sx={{ minWidth: 250, flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchInput && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchInput('');
                        setSearch('');
                      }}
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>{t('servicePayments.filters.status')}</InputLabel>
              <Select
                value={paymentStatus}
                onChange={(e) => {
                  setPaymentStatus(e.target.value);
                  setPage(0);
                }}
                label={t('servicePayments.filters.status')}
              >
                <MenuItem value="">{t('servicePayments.filters.all')}</MenuItem>
                <MenuItem value="PENDING">{t('servicePayments.status.PENDING')}</MenuItem>
                <MenuItem value="PROCESSING">{t('servicePayments.status.PROCESSING')}</MenuItem>
                <MenuItem value="COMPLETED">{t('servicePayments.status.COMPLETED')}</MenuItem>
                <MenuItem value="FAILED">{t('servicePayments.status.FAILED')}</MenuItem>
                <MenuItem value="REFUNDED">{t('servicePayments.status.REFUNDED')}</MenuItem>
                <MenuItem value="PARTIALLY_REFUNDED">{t('servicePayments.status.PARTIALLY_REFUNDED')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>{t('servicePayments.filters.method')}</InputLabel>
              <Select
                value={paymentMethod}
                onChange={(e) => {
                  setPaymentMethod(e.target.value);
                  setPage(0);
                }}
                label={t('servicePayments.filters.method')}
              >
                <MenuItem value="">{t('servicePayments.filters.all')}</MenuItem>
                <MenuItem value="CASH">{t('servicePayments.method.CASH')}</MenuItem>
                <MenuItem value="FAWRY">{t('servicePayments.method.FAWRY')}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              type="date"
              label={t('servicePayments.filters.fromDate')}
              size="small"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />
            <TextField
              type="date"
              label={t('servicePayments.filters.toDate')}
              size="small"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />
            {(paymentStatus || paymentMethod || dateFrom || dateTo || search) && (
              <Button
                size="small"
                onClick={() => {
                  setPaymentStatus('');
                  setPaymentMethod('');
                  setDateFrom('');
                  setDateTo('');
                  setSearchInput('');
                  setSearch('');
                  setPage(0);
                }}
              >
                {t('servicePayments.buttons.clearFilters')}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Payments Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {paymentsColumns.map((column) => (
                  <TableCell key={column.id} sx={{ fontWeight: 600 }}>
                    {column.label}
                  </TableCell>
                ))}
                {canViewPaymentDetails && (
                  <TableCell sx={{ fontWeight: 600, width: 50 }}>{t('common.actions')}</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentsLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    {paymentsColumns.map((column) => (
                      <TableCell key={column.id}>
                        <Skeleton variant="text" width="100%" />
                      </TableCell>
                    ))}
                    {canViewPaymentDetails && (
                      <TableCell>
                        <Skeleton variant="circular" width={40} height={40} />
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : paymentsData?.results && paymentsData.results.length > 0 ? (
                paymentsData.results.map((payment: Payment) => (
                  <TableRow key={payment.id}>
                    <TableCell>{payment.id}</TableCell>
                    <TableCell>
                      {canViewOrderDetails ? (
                        <Box
                          onClick={() => navigate(`/orders/${payment.order.id}`)}
                          sx={{
                            fontWeight: 600,
                            color: 'primary.main',
                            cursor: 'pointer',
                            '&:hover': {
                              textDecoration: 'underline',
                              color: 'primary.dark',
                            },
                          }}
                        >
                          {payment.order.order_number}
                        </Box>
                      ) : (
                        <Box sx={{ fontWeight: 600, color: 'text.primary' }}>
                          {payment.order.order_number}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {payment.user.full_name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {payment.provider ? (
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {payment.provider.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {payment.provider.type}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t('servicePayments.na')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(payment.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(`servicePayments.method.${payment.payment_method}`, payment.payment_method)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <PaymentListStatusTrigger
                        payment={payment}
                        canUpdate={canUpdatePaymentStatus}
                        updating={updatingPaymentId === payment.id}
                        onOpenStatusMenu={openPaymentStatusMenu}
                      />
                    </TableCell>
                    <TableCell>
                      {moment(payment.created_at).format('MMM DD, YYYY HH:mm')}
                    </TableCell>
                    <TableCell>
                      {payment.paid_at
                        ? moment(payment.paid_at).format('MMM DD, YYYY HH:mm')
                        : t('servicePayments.na')}
                    </TableCell>
                    {canViewPaymentDetails && (
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, payment)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={paymentsColumns.length + (canViewPaymentDetails ? 1 : 0)} align="center" sx={{ py: 5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('servicePayments.empty')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {paymentsData && (
          <TablePagination
            component="div"
            count={paymentsData.count || 0}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        )}
      </Card>

      {/* Actions Menu */}
      {canViewPaymentDetails && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          {selectedPayment && (
            <MenuItem
              onClick={() => {
                navigate(`/payments/${selectedPayment.id}`);
                handleMenuClose();
              }}
            >
              <ViewIcon sx={{ mr: 1 }} fontSize="small" />
              {t('servicePayments.buttons.viewDetails')}
            </MenuItem>
          )}
        </Menu>
      )}

      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor && statusMenuPayment)}
        onClose={closePaymentStatusMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          elevation: 8,
          sx: { minWidth: 220, maxHeight: 360, mt: 0.5 },
        }}
      >
        {statusMenuSplit.progress.length > 0 && (
          <ListSubheader sx={{ lineHeight: '32px', fontWeight: 600 }}>{t('servicePayments.menu.nextStatus')}</ListSubheader>
        )}
        {statusMenuSplit.progress.map((key) => (
          <MenuItem key={key} onClick={() => handlePaymentStatusMenuPick(key)} dense>
            {PAYMENT_STATUS_LABELS[key]}
          </MenuItem>
        ))}
        {statusMenuSplit.markFailed.length > 0 && (
          <ListSubheader sx={{ lineHeight: '32px', fontWeight: 600 }}>{t('servicePayments.menu.markAsFailed')}</ListSubheader>
        )}
        {statusMenuSplit.markFailed.map((key) => (
          <MenuItem
            key={key}
            onClick={() => handlePaymentStatusMenuPick(key)}
            dense
            sx={{ color: 'error.main' }}
          >
            {PAYMENT_STATUS_LABELS[key]}
          </MenuItem>
        ))}
        {statusMenuSplit.refunds.length > 0 && (
          <ListSubheader sx={{ lineHeight: '32px', fontWeight: 600 }}>{t('servicePayments.menu.refunds')}</ListSubheader>
        )}
        {statusMenuSplit.refunds.map((key) => (
          <MenuItem key={key} onClick={() => handlePaymentStatusMenuPick(key)} dense>
            {PAYMENT_STATUS_LABELS[key]}
          </MenuItem>
        ))}
      </Menu>

      <Dialog open={statusDialogOpen} onClose={closePaymentStatusDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('servicePayments.dialog.title')}</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {statusDialogPayment && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('servicePayments.dialog.currentStatus')}{' '}
              <strong>
                {PAYMENT_STATUS_LABELS[statusDialogPayment.status as PaymentStatus] ?? statusDialogPayment.status}
              </strong>
              {'. '}{t('servicePayments.dialog.transitionsHint')}
            </Typography>
          )}
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>{t('servicePayments.dialog.newStatus')}</InputLabel>
            <Select
              value={statusNewStatus}
              label={t('servicePayments.dialog.newStatus')}
              onChange={(e) => setStatusNewStatus(e.target.value as UpdatePaymentStatusData['status'])}
              MenuProps={{ PaperProps: { style: { maxHeight: 360 } } }}
            >
              {statusDialogSplit.progress.length > 0 && (
                <ListSubheader sx={{ lineHeight: '32px', fontWeight: 600 }}>{t('servicePayments.menu.nextStatus')}</ListSubheader>
              )}
              {statusDialogSplit.progress.map((key) => (
                <MenuItem key={key} value={key}>
                  {PAYMENT_STATUS_LABELS[key]}
                </MenuItem>
              ))}
              {statusDialogSplit.markFailed.length > 0 && (
                <ListSubheader sx={{ lineHeight: '32px', fontWeight: 600 }}>{t('servicePayments.menu.markAsFailed')}</ListSubheader>
              )}
              {statusDialogSplit.markFailed.map((key) => (
                <MenuItem key={key} value={key}>
                  {PAYMENT_STATUS_LABELS[key]}
                </MenuItem>
              ))}
              {statusDialogSplit.refunds.length > 0 && (
                <ListSubheader sx={{ lineHeight: '32px', fontWeight: 600 }}>{t('servicePayments.menu.refunds')}</ListSubheader>
              )}
              {statusDialogSplit.refunds.map((key) => (
                <MenuItem key={key} value={key}>
                  {PAYMENT_STATUS_LABELS[key]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {statusNewStatus === 'FAILED' && (
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t('servicePayments.dialog.failureReason')}
              value={statusFailureReason}
              onChange={(e) => setStatusFailureReason(e.target.value)}
              sx={{ mb: 2 }}
              required
            />
          )}

          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('servicePayments.dialog.adminNotes')}
            value={statusAdminNotes}
            onChange={(e) => setStatusAdminNotes(e.target.value)}
            placeholder={t('servicePayments.dialog.adminNotesPlaceholder')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closePaymentStatusDialog}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handlePaymentStatusDialogSubmit}
            disabled={
              !statusNewStatus ||
              isPaymentStatusUpdating ||
              (statusNewStatus === 'FAILED' && !statusFailureReason.trim())
            }
          >
            {isPaymentStatusUpdating ? t('servicePayments.buttons.updating') : t('servicePayments.buttons.updateStatus')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentsListPage;

